import { Alert, Table, TableColumnsType } from 'antd';
import React from 'react';
import { useParams } from 'react-router';

import { useGetClinVarGeneDistributionQuery } from '../../api/variantApi';
import { NOTIFICATION_MESSAGES } from '../constants';
import { ClinVarGeneDistributionRow } from '../redux';

type ClinVarGeneDistributionTableRow = ClinVarGeneDistributionRow & {
  key: string;
  classification: string;
};

const renderCount = (value: number, record: ClinVarGeneDistributionTableRow) =>
  record.key === 'all' ? <strong>{value}</strong> : value;

const clinVarGeneDistributionColumns: TableColumnsType<ClinVarGeneDistributionTableRow> =
  [
    {
      title: 'Distribution known variants in gene',
      dataIndex: 'classification',
      key: 'classification',
      render: (text: string, record) =>
        record.key === 'all' ? <strong>{text}</strong> : text,
    },
    {
      title: 'pLoF',
      dataIndex: 'plof',
      key: 'plof',
      render: renderCount,
    },
    {
      title: 'Missense',
      dataIndex: 'missense',
      key: 'missense',
      render: renderCount,
    },
    {
      title: 'Inframe Indel',
      dataIndex: 'inframe_indel',
      key: 'inframe_indel',
      render: renderCount,
    },
    {
      title: 'Synonymous',
      dataIndex: 'synonymous',
      key: 'synonymous',
      render: renderCount,
    },
    {
      title: 'Other',
      dataIndex: 'other',
      key: 'other',
      render: renderCount,
    },
    {
      title: 'all',
      dataIndex: 'all',
      key: 'all',
      render: renderCount,
    },
  ];

/**
 * Renders the "Distribution known variants in gene" table: all ClinVar
 * variants in the gene grouped by variant consequence (columns) and
 * clinical significance (rows). Used to assess the disease mechanism
 * (e.g. LoF vs. missense) of the gene.
 */
const ClinVarGeneDistributionSection = () => {
  const params = useParams();

  const {
    data: distribution,
    isError,
    isLoading,
  } = useGetClinVarGeneDistributionQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  if (!distribution && !isLoading && !isError) {
    return null;
  }

  const dataSource: ClinVarGeneDistributionTableRow[] = distribution
    ? [
        {
          key: 'p_lp',
          classification: 'Pathogenic/Likely Pathogenic',
          ...distribution.pathogenic_likely_pathogenic,
        },
        {
          key: 'vus_conflicting',
          classification: 'Uncertain significance/Conflicting',
          ...distribution.uncertain_significance_conflicting,
        },
        {
          key: 'b_lb',
          classification: 'Benign/Likely benign',
          ...distribution.benign_likely_benign,
        },
        {
          key: 'other',
          classification: 'Other',
          ...distribution.other,
        },
        {
          key: 'all',
          classification: 'All',
          ...distribution.all,
        },
      ]
    : [];

  return (
    <div className="molecular-impact__gene-distribution-section">
      {isError && (
        <Alert
          type="error"
          showIcon
          message={NOTIFICATION_MESSAGES.ERROR}
          description={
            NOTIFICATION_MESSAGES.CLINVAR_GENE_DISTRIBUTION_LOAD_FAILED
          }
        />
      )}
      {(distribution || isLoading) && (
        <Table
          columns={clinVarGeneDistributionColumns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default ClinVarGeneDistributionSection;
